/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import CampaignModel from 'cio/models/campaign';
import { action } from '@ember/object';
import { DateRange } from '@customerio/fly-components/utils/fly-date-picker-utils';
import { deliveryStatesAndActionsPerType } from 'cio/utils/action-info';
import { underscore } from '@ember/string';
import { Metric } from 'cio/utils/metrics/models/metric';
import { inject as service } from '@ember/service';
import DS from 'ember-data';
import { LEADERBOARD_TABS } from 'cio/utils/campaign-overview/leaderboard';
import { SortDirection } from 'cio/utils/sort';
import AnalyticsService from 'cio/services/analytics';
import { ClickSource } from 'cio/utils/analytics/transforms/campaign';
import ActionModel from 'cio/models/action';
import SettingsService from 'cio/services/settings';

interface CampaignOverviewPageLeaderboardCardArgs {
  campaign: CampaignModel;
  timeRange: DateRange;
  channels: string[];
  actionDeliveryMetrics: Record<string, Metric>;
  isLoadingMetrics: boolean;
  tab: string;
  onTabChange: (tab: string) => unknown;
  sortDirection: SortDirection;
  onSortDirectionChange: (sortDirection: SortDirection) => unknown;
}

export default class CampaignOverviewPageLeaderboardCard extends Component<CampaignOverviewPageLeaderboardCardArgs> {
  @service analytics!: AnalyticsService;
  @service store!: DS.Store;
  @service settings!: SettingsService;

  tabs = [...LEADERBOARD_TABS];

  get channelActionTypes() {
    return this.args.channels.map((channel) => `${channel}_action`);
  }

  get availableActionTypes() {
    return Object.entries(deliveryStatesAndActionsPerType(true))
      .filter(
        ([actionType, measures]) =>
          this.channelActionTypes.includes(actionType) && measures.includes(underscore(this.args.tab)),
      )
      .map(([actionType, _]) => actionType);
  }

  get actionDeliveryMetricsWithActions() {
    return Object.keys(this.args.actionDeliveryMetrics).map((id) => ({
      actionModel: this.store.peekRecord('action', id),
      metric: this.args.actionDeliveryMetrics[id],
    }));
  }

  get items() {
    this.args.campaign.ensureParsedTree();

    return this.actionDeliveryMetricsWithActions
      .filter((a) => a.actionModel?.type && this.availableActionTypes.includes(a.actionModel.type))
      .map((a) => {
        let { name, id } = a.actionModel!;

        let language = this.args.campaign.getConsolidatedBranchChildActionLanguage(id);
        if (language != null && name != null) {
          if (language === '') {
            language = 'Default';
          }

          name += ` (${language})`;

          let defaultAction = this.args.campaign.getConsolidatedActionsGroupDefaultActionByChildActionId(id);
          if (defaultAction != null) {
            id = defaultAction.id;
          }
        }

        let channel = a.actionModel?.type.replace('_action', '') ?? '';
        let metrics = {
          total_delivered: a.metric.delivered.total,
          sent_rate: a.metric.sent.rate,
          total_sent: a.metric.sent.total,
          total_opened: a.metric.opened?.total ?? 0,
          opened_rate: a.metric.opened?.rate ?? 0,
          total_clicked: a.metric.clicked.total,
          clicked_rate: a.metric.clicked.rate,
          total_converted: a.metric.converted.total,
          converted_rate: a.metric.converted.rate,
        };
        return {
          id,
          icon: a.actionModel?.info.icon,
          type: 'action',
          actionType: a.actionModel?.type,
          campaignType: this.args.campaign.type,
          metric: a.metric,
          name,
          data: {
            delivery_metrics: metrics,
            per_channel_metrics: {
              [channel]: metrics,
            },
          },
          conversion: a.metric.canConvert,
        };
      });
  }

  @action
  trackNavigate(destination: string, _sortDirection: SortDirection, _activeTab: string, itemId: string) {
    if (destination === 'vwb') {
      this.analytics.track('campaign', 'workflow_existing_item_clicked', {
        model: this.args.campaign,
        action: this.store.peekRecord('action', itemId) as ActionModel,
        trayTab: 'build',
        source: ClickSource.OverviewLeaderboardCard,
      });
    }
  }
}
